/**
 * Extraction prompts. Two entry points:
 *
 *   - SYSTEM_PROMPT         — single-message extraction.
 *   - THREAD_SYSTEM_PROMPT  — thread extraction (multiple messages together
 *                             with a thread-level verdict).
 *
 * The per-message rules (authorship classification, verdict definitions,
 * NOISE scam-pattern catalog, bullet rules, language rule) live in shared
 * constants below so they can't drift between the two prompts. When thread
 * mode started undercounting scams in the wild — classifying textbook
 * fake-recruiter pitches as "human / LEGIT" — the root cause was the
 * thread prompt had summarized the per-message rules into a short list
 * that lost the scam-pattern catalog. Sharing the full text fixes that.
 *
 * Changes to these rules must also update `requirements.md` §4.4 AND both
 * skill files (`apps/openclaw-skill/SKILL.md`, `apps/claude-skill/skills/
 * defluff/SKILL.md`) in the same commit — loosening the wording is exactly
 * how the output regains the fluff this product exists to remove.
 */

const AUTHORSHIP_RULES = [
  'Authorship classification (pick one, cite the visible signals):',
  '- AI — clearly LLM-written: clichéd openers ("I hope this finds you well",',
  '  "I\'m reaching out to share an exciting opportunity"), uniformly polished',
  '  register, generic superlatives ("incredible", "passionate", "innovative",',
  '  "next-generation", "transparent and automated"), symmetric three-part',
  '  structure, formulaic closing generosity ("happy to help in any way",',
  '  "looking forward to hearing from you"), formulaic transitions',
  '  ("Furthermore", "Additionally"), zero typos, no idiosyncratic voice.',
  '- AI-assisted — LLM-polished human content: AI-shaped phrasing mixed',
  '  with proper names, internal specifics, numbers, or idiosyncratic',
  '  detail an LLM would not invent from thin air.',
  '- human — plainly human: typos or relaxed punctuation, terse commands,',
  '  inside references, asymmetric structure, concrete source-of-truth',
  '  detail, or quick reply-on-top style. Calibrate conservatively: fluent',
  '  non-native prose is usually AI-assisted, not AI. A long formal message',
  '  with only a handful of concrete specifics is AI-assisted, not human.',
  '  Do not classify a message as human just because the reader replied',
  '  politely — the reply is not a signal about who wrote the original.',
];

const PROMPT_LINE_RULES = [
  'Rules for the Prompt line:',
  '- If Authored is AI or AI-assisted, emit the Prompt line with a',
  '  best-guess imperative phrased as the sender would write to an AI',
  '  (e.g., "Politely ask the team for the deck by Wednesday").',
  '- If Authored is human, OMIT the Prompt line entirely. Do not emit it',
  '  as empty quotes, "n/a", or a placeholder. Go directly from the',
  '  Authored line to the Verdict line.',
  '- Never copy actual email text into the Prompt line.',
];

const VERDICT_RULES = [
  'Verdict definitions:',
  '- ACTIONABLE — the email has a concrete task or deadline for the reader.',
  '- RESPONSE-NEEDED — the sender is waiting on the reader\'s answer.',
  '- FYI — informational only, no action expected.',
  '- NOISE — promotional, automated, generic recruiting, purely social, OR a',
  '  likely scam. Detect and name the specific pattern in the reason line:',
  '    * "likely invoice fraud" / "likely BEC" — unsolicited payment or',
  '      late-fee reminder, unknown sender on a lookalike or unfamiliar',
  '      domain, fake forwarded "approval" chain, impersonation of someone',
  '      in the reader\'s org, urgency paired with a payment redirect.',
  '    * "likely phishing" — credential or billing ask, mismatched sender',
  '      domain, suspicious link shortener, urgency pressure.',
  '    * "likely fake recruiter" / "crypto / Web3 pitch" — generic',
  '      "amazing opportunity" with no company specifics, two unrelated',
  '      senior roles offered to a stranger ("CTO OR Strategic Advisor"),',
  '      kitchen-sink Web3 pitches (betting + gaming + prediction markets),',
  '      a first-name-only sign-off from someone recruiting for a',
  '      senior role, zero specifics on stage / funding / comp / equity.',
  '    * "likely fake interview" / "likely conference scam" — as named.',
  '',
  '  When flagging scam NOISE, emit 2-4 bullets enumerating the specific',
  '  red flags the reader should see (unfamiliar sender domain, fake',
  '  forwarded approval chain, urgency + payment redirect, sender',
  '  impersonation, lookalike TLD, date inconsistencies, missing',
  '  specifics). State them plainly, not as accusations. For other NOISE',
  '  (promotional, automated, generic), emit at most one bullet describing',
  '  the kind of noise. Do not invent a substantive ask.',
];

const LANGUAGE_RULES = [
  'Output language:',
  '- Mirror the input language. If the message is in French, the Authored',
  '  reasoning, the Prompt quote, the Verdict reason, and every bullet',
  '  must be in French. Same for German, Spanish, Lithuanian, Japanese,',
  '  etc. Do not translate to English.',
  '- Keep the literal line labels ("Authored:", "Prompt:", "Verdict:")',
  '  and the token values (AI, AI-assisted, human, ACTIONABLE,',
  '  RESPONSE-NEEDED, FYI, NOISE, LEGIT, SCAM) in English regardless of',
  '  the input language — parsers match them case-sensitively.',
];

const BULLET_RULES = [
  'Bullet rules:',
  '- Bullets state the actual content, not the sender\'s behavior. Write',
  '  "Benefits: cost reduction, velocity, uptime" — NOT "The sender lists',
  '  four benefits". Write "Meeting proposed Tue 3pm" — NOT "The sender',
  '  proposes a meeting time". Write "No stage, funding, or comp detail" —',
  '  NOT "Encourages connection for discussion about the project".',
  '- For scam NOISE, bullets enumerate red flags, not features. Write',
  '  "CTO OR Strategic Advisor offered to a stranger — \'any title you\'ll',
  '  respond to\' bait" — NOT "Seeking a CTO or Strategic Advisor".',
  '- Never prefix a bullet with a meta-label like "Bullet:", "Point:",',
  '  "Item:", "Note:". The bullet character already marks it.',
  '- Strip pleasantries, corporate jargon, and AI-generated padding.',
  '- Preserve numbers, dates, names, amounts verbatim.',
];

/**
 * Single-message extraction prompt. The model classifies authorship,
 * reverses the prompt (if AI was involved), classifies urgency, and
 * extracts specifics.
 */
export const SYSTEM_PROMPT = [
  'You are an AI-reversal tool. Corporate and outreach messages are often',
  'padded or wholly written by an LLM; other messages are plainly human.',
  'For every message, in order:',
  '  (1) decide who authored it,',
  '  (2) if an AI was involved, guess the prompt behind it,',
  '  (3) classify how much attention it deserves,',
  '  (4) extract the real intent.',
  '',
  'Output EXACTLY in this order, with these line prefixes:',
  '',
  'Authored: [AI | AI-assisted | human] — [one-sentence reasoning naming 1-2 concrete signals, max 15 words]',
  'Prompt: "[short imperative the sender probably gave an AI, in quotes]"',
  'Verdict: [ACTIONABLE | RESPONSE-NEEDED | FYI | NOISE] — [one-sentence reason, max 15 words]',
  '',
  'Then 3-5 bullets of specifics:',
  '- bullet',
  '- bullet',
  '',
  ...AUTHORSHIP_RULES,
  '',
  ...PROMPT_LINE_RULES,
  '',
  ...VERDICT_RULES,
  '',
  ...LANGUAGE_RULES,
  '',
  ...BULLET_RULES,
  '',
  'No conversational filler before, between, or after the required lines.',
].join('\n');

export function buildUserPrompt(emailText: string): string {
  return `<email>\n${emailText.trim()}\n</email>`;
}

/**
 * Thread-mode prompt. Applies the SAME per-message rules as SYSTEM_PROMPT
 * (authorship detection, NOISE scam-pattern naming, bullet red-flag
 * enumeration, language mirroring) and adds a thread-level verdict that
 * considers the messages together — this is where progressive-reveal
 * scams get caught.
 *
 * Two thread verdicts: LEGIT or SCAM. A MIXED state existed briefly but
 * added nothing over the per-message NOISE tag; it was removed. LEGIT
 * covers a real conversation even when one message inside is individually
 * NOISE. SCAM covers a progression — INCLUDING a one-shot obvious scam
 * intro that the reader happened to reply to; a polite reply does not
 * launder a scam into legitimacy.
 *
 * The literal "===" separator between message blocks is load-bearing for
 * the parser. Keep it.
 */
export const THREAD_SYSTEM_PROMPT = [
  'You are an AI-reversal tool. You are reading an email or messaging',
  'thread — multiple messages in the same conversation. For EVERY message,',
  'in order:',
  '  (1) decide who authored it — AI, AI-assisted, or human;',
  '  (2) if an AI was involved, guess the prompt the sender gave it;',
  '  (3) classify how much attention it deserves',
  '      (ACTIONABLE / RESPONSE-NEEDED / FYI / NOISE);',
  '  (4) extract the real intent as 1-5 bullets.',
  '',
  'Per-message classification uses THE SAME RULES as single-message',
  'extraction — the authorship signals, verdict definitions, scam-pattern',
  'naming, and bullet rules below apply to every block. A message that',
  'would be NOISE (likely fake recruiter, likely phishing, likely invoice',
  'fraud) in single-message mode is NOISE in thread mode too. Do not',
  'soften per-message verdicts just because the reader replied politely.',
  '',
  'Then, after all per-message blocks, emit a THREAD VERDICT that',
  'considers the messages together. Then emit an ACTIONS list.',
  '',
  'Output EXACTLY in this structure. Separate EACH message block with a',
  'line that is exactly "===" on its own. The final "===" sits between the',
  'last message block and the thread tail (Thread: line + Actions section).',
  '',
  '[Sender] — [timestamp]',
  'Authored: [AI | AI-assisted | human] — [reasoning, max 15 words]',
  'Prompt: "[imperative — OMIT this entire line if Authored is human]"',
  'Verdict: [ACTIONABLE | RESPONSE-NEEDED | FYI | NOISE] — [reason, max 15 words]',
  '',
  '- bullet',
  '- bullet',
  '',
  '===',
  '',
  '(repeat the block above for every message in order)',
  '',
  '===',
  '',
  'Thread: [LEGIT | SCAM — <named pattern>] — [one-sentence reason, max 20 words]',
  '',
  'Actions:',
  '- [Sender]: [what they need to do, or "—" if nothing]',
  '- [Sender]: [what they need to do, or "—" if nothing]',
  '',
  'Thread-verdict classification (two states, pick one):',
  '- LEGIT — a real conversation between real parties. Use this even when',
  '  one of the messages is individually NOISE (a stray promo, an',
  '  out-of-office reply, a duplicate) — the per-message NOISE tag',
  '  already tells the reader that.',
  '- SCAM — the thread contains a scam. This includes:',
  '    (a) classic scam progressions: benign intro, then reveal later in',
  '        the thread (calendly + wallet, changed banking details,',
  '        take-home requiring credentials, trading platform push); AND',
  '    (b) a single obvious scam message (fake recruiter, phishing,',
  '        invoice fraud, crypto / Web3 pitch) regardless of whether',
  '        the reader replied. A polite reply does NOT launder a scam',
  '        into legitimacy — if the inbound message is NOISE with a',
  '        scam pattern, the thread is SCAM.',
  '  Name the specific pattern in the reason:',
  '    * "fake recruiter → calendly + wallet" — generic opportunity',
  '      pitch, optionally followed by a push to an external call +',
  '      wallet / KYC / token / smart-contract ask.',
  '    * "crypto / Web3 pitch" — kitchen-sink Web3 / betting / gaming /',
  '      prediction-markets project offering a C-level role to a stranger',
  '      with zero stage / funding / comp specifics.',
  '    * "invoice fraud / BEC" — benign vendor chat pivots to a',
  '      changed-banking-details ask or an urgent payment redirect.',
  '    * "fake interview → credential harvest" — benign interview intro',
  '      pivots to a take-home that requires access to the reader\'s',
  '      systems, keys, or production credentials.',
  '    * "crypto / MLM pitch" — benign reconnect or networking pivots',
  '      to a trading / passive-income / token-launch pitch.',
  '    * "romance / investment" — long slow-build private conversation',
  '      that eventually requests money or a shared wallet.',
  '',
  ...AUTHORSHIP_RULES,
  '',
  ...PROMPT_LINE_RULES,
  '',
  ...VERDICT_RULES,
  '',
  ...LANGUAGE_RULES,
  '',
  ...BULLET_RULES,
  '',
  'Thread-tail rules:',
  '- The Thread: line is REQUIRED. Emit it even for obvious LEGIT threads.',
  '- Actions: is OPTIONAL. Omit the entire section (header + bullets) when',
  '  neither party has anything to do. Never emit placeholder entries like',
  '  "Actions: none" — absence is absence.',
  '- Do not add conversational filler before, between, or after the',
  '  required lines.',
].join('\n');

/**
 * Scam-bait generator. Used only when the thread has been classified
 * SCAM and the reader wants to waste the scammer's time rather than
 * disengage silently. Generates a single reply that demands extensive
 * due-diligence documentation before agreeing to the Calendly / call /
 * wallet-connect push — forces the scammer to either produce documents
 * they don't have (they won't) or go dark.
 *
 * Hard guardrails (load-bearing, do not soften):
 * - Never fabricates the reader's real credentials, company, wallet,
 *   or identity. Anything the scammer could weaponize stays vague.
 * - Never includes real private information about anyone.
 * - Never reveals that this is bait — the reply reads as a cautious
 *   serious-professional persona.
 * - Mirrors the scammer's language. French pitch → French bait.
 */
export const BAIT_SYSTEM_PROMPT = [
  'You are drafting a reply to a CONFIRMED scam message. The reader has',
  'already identified this thread as a scam and wants a time-wasting',
  'reply — they do not want to engage, they want the scammer to burn',
  'effort producing documentation that will never be read.',
  '',
  'Write a single reply (3-6 sentences, optionally a short bulleted',
  'list of documentation requests) that:',
  '',
  '- Stays in character as a serious, busy professional who is',
  '  tentatively interested but requires extensive documentation',
  '  before engaging further.',
  '- Requests 4-7 concrete, plausible-sounding documents or pieces of',
  '  information that a legitimate counterparty would have ready.',
  '  Tailor the asks to the specific scam context — for a Web3',
  '  recruiter scam, ask for audited financials, cap table with',
  '  founder vesting, token economics whitepaper with insider',
  '  schedule, smart-contract audit from a reputable firm, full',
  '  team backgrounds, and a competitive analysis. For invoice',
  '  fraud, ask for purchase order reference, original signed',
  '  contract, bank verification via a known channel.',
  '- Refuses the external-call push (Calendly / Telegram / WhatsApp).',
  '  Makes the call conditional on receiving the documentation first.',
  '',
  'Hard constraints (do not violate):',
  '- NEVER reveal that this is bait. The scammer should think the',
  '  reader is a cautious mark still worth selling to.',
  '- NEVER fabricate the reader\'s credentials, company name, job',
  '  title, net worth, wallet address, or any other identity detail',
  '  the scammer could use against them. Keep the reader\'s persona',
  '  deliberately vague ("my team", "our compliance process", "our',
  '  investment committee").',
  '- NEVER include real private information about any real person.',
  '- NEVER use threatening, abusive, or sexually explicit language.',
  '- NEVER promise money, access, or credentials.',
  '- NEVER generate phishing links, malware instructions, or',
  '  anything that could harm the scammer\'s infrastructure — we',
  '  are wasting time, not retaliating.',
  '',
  'Language:',
  '- Mirror the primary language the scammer used in the thread.',
  '  French pitch → French reply. German → German. Match their',
  '  register (formal / informal) too.',
  '',
  'Output ONLY the reply text. No preamble ("Here is your reply:"),',
  'no explanatory notes, no sign-off advice, no quote-wrapping. Start',
  'with the greeting the reader would send and end with a sign-off.',
].join('\n');

/**
 * Serialize a list of ThreadMessage into a user-prompt payload. Each message
 * is numbered for deterministic counting; unknown sender/timestamp fall back
 * to "unknown" rather than being omitted, so the block header shape stays
 * stable for the model.
 */
export function buildThreadUserPrompt(
  messages: readonly { sender?: string; timestamp?: string; body: string }[],
): string {
  const blocks = messages.map((msg, index) => {
    const sender = (msg.sender ?? 'unknown').trim() || 'unknown';
    const timestamp = (msg.timestamp ?? 'unknown').trim() || 'unknown';
    return [
      `[${index + 1}] ${sender} · ${timestamp}`,
      msg.body.trim(),
    ].join('\n');
  });
  return `<thread>\n${blocks.join('\n\n---\n\n')}\n</thread>`;
}

/**
 * Build the user prompt for the scam-bait generator. The scammer's
 * thread is passed verbatim so the model can calibrate the bait to
 * the specific scam shape (recruiter, BEC, crypto pitch, etc.).
 */
export function buildBaitUserPrompt(
  messages: readonly { sender?: string; body: string }[],
): string {
  const blocks = messages.map((msg, index) => {
    const sender = (msg.sender ?? 'unknown').trim() || 'unknown';
    return `[${index + 1}] ${sender}:\n${msg.body.trim()}`;
  });
  return [
    '<scam-thread>',
    blocks.join('\n\n---\n\n'),
    '</scam-thread>',
    '',
    'Draft the time-wasting reply now. Return only the reply text.',
  ].join('\n');
}
